<!-- ============================================================
     HOMEWORK — Teacher assigns · Students are informed · Students update
     ============================================================ -->
<div class="hw-page">

  @if (saveSuccess()) {
    <div class="hw-toast alert d-flex align-items-center gap-2 shadow"
         [class.alert-success]="toastKind() === 'success'"
         [class.alert-danger]="toastKind() === 'danger'">
      <i class="bi fs-5" [class.bi-check-circle-fill]="toastKind() === 'success'" [class.bi-exclamation-triangle-fill]="toastKind() === 'danger'"></i>
      <strong>{{ saveSuccess() }}</strong>
    </div>
  }

  <!-- Delete Modal -->
  @if (showDeleteModal()) {
    <div class="hw-overlay" (click)="showDeleteModal.set(false)">
      <div class="hw-modal hw-modal-sm" (click)="$event.stopPropagation()">
        <div class="hmh del-hdr">
          <div class="hmh-icon"><i class="bi bi-trash3-fill"></i></div>
          <div><h5 class="hmh-title">Delete homework?</h5><p class="hmh-sub">{{ deleteCtx()?.label }}</p></div>
          <button class="hmh-close" (click)="showDeleteModal.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="p-4 text-center"><p class="text-muted small mb-0">Submissions and student notifications for this assignment will also be removed.</p></div>
        <div class="hmf">
          <button class="btn btn-outline-secondary px-4" (click)="showDeleteModal.set(false)">Cancel</button>
          <button class="btn btn-danger px-4" (click)="executeDelete()"><i class="bi bi-trash3 me-2"></i>Delete</button>
        </div>
      </div>
    </div>
  }

  <!-- Student update / submit modal -->
  @if (updatingWork()) {
    @let uw = updatingWork()!;
    @let uhw = getHw(uw.homework_id);
    <div class="hw-overlay" (click)="updatingWork.set(null)">
      <div class="hw-modal hw-modal-lg" (click)="$event.stopPropagation()">
        <div class="hmh">
          <div class="hmh-icon"><i class="bi bi-pencil-square"></i></div>
          <div>
            <h5 class="hmh-title">{{ uhw?.title }}</h5>
            <p class="hmh-sub">{{ uhw?.subject }} · Due {{ formatDate(uhw?.due_date ?? '') }} {{ formatTime(uhw?.due_time ?? '') }}</p>
          </div>
          <button class="hmh-close" (click)="updatingWork.set(null)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="hmb p-4">
          @if (uhw) {
            <p class="small text-muted mb-3">{{ uhw.instructions }}</p>
            @if (uhw.documents && uhw.documents.length && uhw.documents[0].name) {
              <div class="file-list mb-3">
                <div class="small fw-semibold text-muted mb-1">Assignment documents</div>
                @for (doc of uhw.documents; track doc.name) {
                  <div class="file-row">
                    <i class="bi {{ fileIcon(doc) }}"></i>
                    <div class="flex-grow-1">
                      <div class="fw-semibold small">{{ doc.name }}</div>
                      <div class="file-size">{{ formatSize(doc.size) }}</div>
                    </div>
                    <button type="button" class="btn btn-sm btn-outline-primary" (click)="downloadDocument(doc, $event)">
                      <i class="bi bi-download me-1"></i>Download
                    </button>
                  </div>
                }
              </div>
            }
          }
          <div class="mb-3">
            <label class="form-label fw-semibold">Your work <span class="req">*</span></label>
            <textarea class="form-control" rows="4" [(ngModel)]="submitForm.work_text"
              placeholder="Write your answer, notes, or a short summary of the attached file…"
              [class.is-invalid]="submitAttempted && !submitForm.work_text.trim() && !submitFile()"></textarea>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-md-7">
              <label class="form-label fw-semibold">Upload your document</label>
              <input type="file" class="form-control" [accept]="acceptedFiles" (change)="onStudentFile($event)"
                [disabled]="uw.status === 'Graded'">
              <small class="text-muted">PDF, Word, Excel, images, or ZIP · max 8 MB</small>
              @if (submitFile()) {
                <div class="file-row mt-2">
                  <i class="bi {{ fileIcon(submitFile()) }}"></i>
                  <div class="flex-grow-1">
                    <div class="fw-semibold small">{{ submitFile()!.name }}</div>
                    <div class="file-size">{{ formatSize(submitFile()!.size) }}</div>
                  </div>
                  <button type="button" class="btn btn-sm btn-outline-primary" (click)="downloadDocument(submitFile()!, $event)"><i class="bi bi-download"></i></button>
                  @if (uw.status !== 'Graded') {
                    <button type="button" class="btn btn-sm btn-outline-danger" (click)="removeStudentFile()"><i class="bi bi-x-lg"></i></button>
                  }
                </div>
              }
            </div>
            <div class="col-md-5">
              <label class="form-label fw-semibold">Note to teacher</label>
              <input class="form-control" [(ngModel)]="submitForm.student_remark" placeholder="Optional" [disabled]="uw.status === 'Graded'">
            </div>
          </div>
          @if (uw.status === 'Graded') {
            <div class="grade-box">
              <div class="fw-bold">Marked {{ uw.marks_obtained }} / {{ uhw?.max_marks }}</div>
              @if (uw.teacher_remark) { <div class="small mt-1">{{ uw.teacher_remark }}</div> }
            </div>
          }
          @if (uw.status === 'Returned') {
            <div class="return-box"><i class="bi bi-exclamation-triangle-fill me-1"></i>{{ uw.teacher_remark || 'Please revise and resubmit.' }}</div>
          }
        </div>
        <div class="hmf">
          <button class="btn btn-outline-secondary" (click)="updatingWork.set(null)">Cancel</button>
          @if (uw.status !== 'Graded') {
            <button class="btn btn-outline-primary" (click)="saveStudentUpdate(false)"><i class="bi bi-floppy me-1"></i>Save progress</button>
            <button class="btn hw-btn-primary" (click)="saveStudentUpdate(true)"><i class="bi bi-send-fill me-1"></i>Submit homework</button>
          }
        </div>
      </div>
    </div>
  }

  <!-- Teacher grade modal -->
  @if (gradingWork()) {
    @let gw = gradingWork()!;
    @let ghw = getHw(gw.homework_id);
    <div class="hw-overlay" (click)="gradingWork.set(null)">
      <div class="hw-modal hw-modal-lg" (click)="$event.stopPropagation()">
        <div class="hmh grade-hdr">
          <div class="hmh-icon"><i class="bi bi-award-fill"></i></div>
          <div>
            <h5 class="hmh-title">Review · {{ gw.student_name }}</h5>
            <p class="hmh-sub">{{ gw.roll_no }} · {{ ghw?.title }}</p>
          </div>
          <button class="hmh-close" (click)="gradingWork.set(null)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="hmb p-4">
          <div class="student-work-preview mb-3">
            <div class="small text-muted fw-semibold mb-1">Student work</div>
            <p class="mb-2">{{ gw.work_text || '—' }}</p>
            @if (gw.file) {
              <div class="file-row mb-2">
                <i class="bi {{ fileIcon(gw.file) }}"></i>
                <div class="flex-grow-1">
                  <div class="fw-semibold small">{{ gw.file.name }}</div>
                  <div class="file-size">{{ formatSize(gw.file.size) }}</div>
                </div>
                <button type="button" class="btn btn-sm btn-outline-primary" (click)="downloadDocument(gw.file!, $event)">
                  <i class="bi bi-download me-1"></i>Download
                </button>
              </div>
            }
            @if (gw.student_remark) { <div class="small text-muted mt-2"><em>Note: {{ gw.student_remark }}</em></div> }
            <div class="small text-muted mt-2">Submitted: {{ formatStamp(gw.submitted_at) }}</div>
          </div>
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label fw-semibold">Marks / {{ ghw?.max_marks ?? 20 }}</label>
              <input type="number" class="form-control" [(ngModel)]="gradeForm.marks_obtained" min="0" [max]="ghw?.max_marks ?? 20"
                [disabled]="gradeForm.return_for_revision"
                [class.is-invalid]="gradeAttempted && !gradeForm.return_for_revision && (gradeForm.marks_obtained === null || gradeForm.marks_obtained! < 0 || gradeForm.marks_obtained! > (ghw?.max_marks ?? 20))">
            </div>
            <div class="col-md-8 d-flex align-items-end">
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" id="retRev" [(ngModel)]="gradeForm.return_for_revision">
                <label class="form-check-label" for="retRev">Return for revision (student will be notified)</label>
              </div>
            </div>
            <div class="col-12">
              <label class="form-label fw-semibold">Feedback</label>
              <textarea class="form-control" rows="3" [(ngModel)]="gradeForm.teacher_remark" placeholder="Comments the student will see…"></textarea>
            </div>
          </div>
        </div>
        <div class="hmf">
          <button class="btn btn-outline-secondary" (click)="gradingWork.set(null)">Cancel</button>
          <button class="btn hw-btn-primary" (click)="saveGrade()">
            <i class="bi" [class.bi-arrow-counterclockwise]="gradeForm.return_for_revision" [class.bi-check-circle-fill]="!gradeForm.return_for_revision"></i>
            {{ gradeForm.return_for_revision ? 'Return to student' : 'Save Grade & Notify' }}
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ===================== PAGE HEADER ===================== -->
  <div class="hw-ph mb-4">
    <div>
      <h1 class="hw-title"><i class="bi bi-journal-text me-2 text-primary" style="font-size:1.1rem;"></i>Homework</h1>
      <p class="hw-subtitle">Teachers assign work · students are informed · students update and submit</p>
    </div>
    <!-- <div class="d-flex gap-2 flex-wrap align-items-center">
      @if (role() === 'student') {
        <select class="form-select form-select-sm student-picker" [ngModel]="currentStudentId()" (ngModelChange)="switchStudent($event)">
          @for (s of roster; track s.id) {
            <option [value]="s.id">{{ s.name }} · {{ s.class_name }}</option>
          }
        </select>
      }
      @if (role() === 'teacher' && activeTab() === 'assignments' && hwMode() === 'list') {
        <button class="btn hw-btn-primary btn-sm" (click)="openNewAssignment()"><i class="bi bi-plus-circle-fill me-1"></i>Assign homework</button>
      }
      @if (role() === 'teacher' && hwMode() !== 'list') {
        <button class="btn btn-outline-secondary btn-sm" (click)="hwMode.set('list')"><i class="bi bi-arrow-left me-1"></i>Back</button>
      }
    </div> -->
  </div>

  <!-- Stats -->
  @if (role() === 'teacher') {
    <div class="hw-stats-row mb-4">
      <div class="hsr-item"><div class="hsr-icon" style="background:linear-gradient(135deg,#1a3a6b,#2d6a9f)"><i class="bi bi-journal-text"></i></div><div><div class="hsr-val">{{ teacherStats().total }}</div><div class="hsr-label">Assignments</div></div></div>
      <div class="hsr-item"><div class="hsr-icon" style="background:linear-gradient(135deg,#059669,#10b981)"><i class="bi bi-megaphone-fill"></i></div><div><div class="hsr-val text-success">{{ teacherStats().published }}</div><div class="hsr-label">Published</div></div></div>
      <div class="hsr-item"><div class="hsr-icon" style="background:linear-gradient(135deg,#6b7280,#9ca3af)"><i class="bi bi-file-earmark"></i></div><div><div class="hsr-val">{{ teacherStats().drafts }}</div><div class="hsr-label">Drafts</div></div></div>
      <div class="hsr-item"><div class="hsr-icon" style="background:linear-gradient(135deg,#d97706,#f59e0b)"><i class="bi bi-inbox-fill"></i></div><div><div class="hsr-val text-warning">{{ teacherStats().pending }}</div><div class="hsr-label">To review</div></div></div>
      <div class="hsr-item"><div class="hsr-icon" style="background:linear-gradient(135deg,#7c3aed,#a78bfa)"><i class="bi bi-award-fill"></i></div><div><div class="hsr-val text-purple">{{ teacherStats().graded }}</div><div class="hsr-label">Graded</div></div></div>
    </div>
  } @else {
    <div class="hw-stats-row mb-4">
      <div class="hsr-item"><div class="hsr-icon" style="background:linear-gradient(135deg,#1a3a6b,#2d6a9f)"><i class="bi bi-journal-plus"></i></div><div><div class="hsr-val">{{ studentStats().assigned }}</div><div class="hsr-label">Assigned</div></div></div>
      <div class="hsr-item"><div class="hsr-icon" style="background:linear-gradient(135deg,#0284c7,#38bdf8)"><i class="bi bi-hourglass-split"></i></div><div><div class="hsr-val text-info">{{ studentStats().inProgress }}</div><div class="hsr-label">In progress</div></div></div>
      <div class="hsr-item"><div class="hsr-icon" style="background:linear-gradient(135deg,#059669,#10b981)"><i class="bi bi-send-check-fill"></i></div><div><div class="hsr-val text-success">{{ studentStats().submitted }}</div><div class="hsr-label">Submitted</div></div></div>
      <div class="hsr-item" [class.stat-alert]="studentStats().overdue > 0"><div class="hsr-icon" [style.background]="studentStats().overdue > 0 ? 'linear-gradient(135deg,#dc2626,#ef4444)' : 'linear-gradient(135deg,#6b7280,#9ca3af)'"><i class="bi bi-exclamation-triangle-fill"></i></div><div><div class="hsr-val" [class.text-danger]="studentStats().overdue > 0">{{ studentStats().overdue }}</div><div class="hsr-label">Overdue</div></div></div>
      <div class="hsr-item"><div class="hsr-icon" style="background:linear-gradient(135deg,#7c3aed,#a78bfa)"><i class="bi bi-award-fill"></i></div><div><div class="hsr-val text-purple">{{ studentStats().graded }}</div><div class="hsr-label">Graded</div></div></div>
    </div>
  }

  <!-- TABS -->
  <div class="hw-tabs mb-4 w-100 d-flex;">
    <div class="role-switch me-4">
      <button class="rbtn" [class.active]="role() === 'teacher'" (click)="switchRole('teacher')">
        <i class="bi bi-person-badge-fill me-1"></i>Teacher
      </button>
      <button class="rbtn" [class.active]="role() === 'student'" (click)="switchRole('student')">
        <i class="bi bi-mortarboard-fill me-1"></i>Student
      </button>
    </div>

    @if (role() === 'teacher') {
      <button class="htab" [class.active]="activeTab() === 'assignments'" (click)="activeTab.set('assignments'); hwMode.set('list')">
        <i class="bi bi-journal-plus me-1"></i>Assignments
      </button>
      <button class="htab" [class.active]="activeTab() === 'review'" (click)="activeTab.set('review'); hwMode.set('list')">
        <i class="bi bi-clipboard-check me-1"></i>Review submissions
      </button>
    } @else {
      <button class="htab" [class.active]="activeTab() === 'my-homework'" (click)="activeTab.set('my-homework')">
        <i class="bi bi-journal-check me-1"></i>My homework
      </button>
      <button class="htab" [class.active]="activeTab() === 'inbox'" (click)="activeTab.set('inbox')">
        <i class="bi bi-bell me-1"></i>Inbox
        @if (unreadAlertCount() > 0) {
          <span class="tab-badge">{{ unreadAlertCount() }}</span>
        }
      </button>
    }

    @if (role() === 'student') {
      <select class="form-select form-select-sm student-picker ms-auto" 
        [ngModel]="currentStudentId()" (ngModelChange)="switchStudent($event)"
        style="width: 250px;">
        @for (s of roster; track s.id) {
          <option [value]="s.id">{{ s.name }} · {{ dataService.getClassName(s.class_name) }}</option>
        }
      </select>
    }

    @if (role() === 'teacher' && activeTab() === 'assignments' && hwMode() === 'list') {
      <button class="btn hw-btn-primary btn-sm ms-auto" (click)="openNewAssignment()"><i class="bi bi-plus-circle-fill me-1"></i>Assign Homework</button>
    }
    @if (role() === 'teacher' && hwMode() !== 'list') {
      <button class="btn btn-outline-secondary btn-sm ms-auto" (click)="hwMode.set('list')"><i class="bi bi-arrow-left me-1"></i>Back</button>
    }
  </div>

  <!-- @if (unreadAlertCount() > 0) {
    <div class="inbox-banner mb-4" (click)="activeTab.set('inbox')">
      <i class="bi bi-bell-fill"></i>
      <span>You have <strong>{{ unreadAlertCount() }}</strong> new homework {{ unreadAlertCount() === 1 ? 'notification' : 'notifications' }}.</span>
      <span class="ms-auto small fw-semibold">Open inbox →</span>
    </div>
  } -->

  <!-- ===================== TEACHER · ASSIGNMENTS ===================== -->
  @if (role() === 'teacher' && activeTab() === 'assignments') {

    @if (hwMode() === 'list') {
      <div class="hw-card mb-3"><div class="hc-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-4"><label class="form-label small fw-semibold text-muted">Search</label>
            <div class="input-group"><span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
              <input class="form-control border-start-0" placeholder="Title, subject, teacher…" [ngModel]="hwSearch()" (ngModelChange)="hwSearch.set($event)">
              @if (hwSearch()) { <button class="btn btn-outline-secondary" (click)="hwSearch.set('')"><i class="bi bi-x"></i></button> }
            </div></div>
          <div class="col-md-3"><label class="form-label small fw-semibold text-muted">Class</label>
            <select class="form-select" [ngModel]="hwClassFilter()" (ngModelChange)="hwClassFilter.set($event)">
              <option value="">All classes</option>
              @for (c of classOptions; track c) { <option [value]="c.code">{{ c.name }}</option> }
            </select></div>
          <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Subject</label>
            <select class="form-select" [ngModel]="hwSubjectFilter()" (ngModelChange)="hwSubjectFilter.set($event)">
              <option value="">All</option>
              @for (s of subjectOpts; track s) { <option [value]="s.code">{{ s.name }}</option> }
            </select></div>
          <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Status</label>
            <select class="form-select" [ngModel]="hwStatusFilter()" (ngModelChange)="hwStatusFilter.set($event)">
              <option value="">All</option>
              @for (s of hwStatuses; track s) { <option [value]="s">{{ s }}</option> }
            </select></div>
          <div class="col-md-1"><button class="btn btn-outline-secondary" (click)="resetHwFilters()"><i class="bi bi-arrow-counterclockwise me-1"></i>Reset</button></div>
        </div>
        <small class="text-muted mt-2 d-block">#{{ filteredAssignments().length }} assignment{{ filteredAssignments().length === 1 ? '' : 's' }}</small>
      </div></div>

      @if (filteredAssignments().length === 0) {
        <div class="empty-state"><i class="bi bi-journal-x"></i><p>No homework found</p></div>
      } @else {
        <div class="hw-grid">
          @for (a of filteredAssignments(); track a.id) {
            <div class="hw-card-item" [style.--hw-color]="getSubjectColor(a.subject)">
              <div class="hci-top">
                <div class="hci-subject" [style.color]="getSubjectColor(a.subject)">{{ dataService.getSubjectName(a.subject) }}</div>
                <div class="d-flex gap-1 align-items-center">
                  <span class="{{ getPriorityClass(a.priority) }}">{{ a.priority }}</span>
                  <span class="{{ getHwStatusClass(a.status) }}">{{ a.status }}</span>
                </div>
              </div>
              <h5 class="hci-name">{{ a.title }}</h5>
              <div class="hci-class"><i class="bi bi-people-fill me-1 text-primary"></i>
                Class: {{ dataService.getClassName(a.class_name) }} · Sec {{ a.section }}
              </div>
              <div class="hci-dates">
                <span><i class="bi bi-calendar-plus me-1"></i>{{ formatDate(a.assigned_date) }}</span>
                <span class="ecd-sep">→</span>
                <span [class.text-danger]="a.status === 'Published' && daysUntilDue(a) < 0"><i class="bi bi-calendar-event me-1"></i>{{ formatDate(a.due_date) }} {{ formatTime(a.due_time) }}</span>
              </div>
              <div class="hci-meta">
                <span><i class="bi bi-person me-1"></i>{{ a.teacher_name }}</span>
                <span><i class="bi bi-star me-1"></i>{{ a.max_marks }} marks</span>
                <span><i class="bi bi-people me-1"></i>{{ submissionCount(a.id) }} students</span>
                <span><i class="bi bi-check2-all me-1"></i>{{ submissionCount(a.id, ['Submitted','Late','Graded']) }} submitted</span>
              </div>
              <p class="hci-desc">{{ a.instructions }}</p>
              @if (a.documents && a.documents.length && a.documents[0].name) {
                <div class="hci-docs">
                  @for (doc of a.documents; track doc.name) {
                    <button type="button" class="doc-chip" (click)="downloadDocument(doc, $event)" title="Download {{ doc.name }}">
                      <i class="bi {{ fileIcon(doc) }} me-1"></i>{{ doc.name }}
                      <i class="bi bi-download ms-1"></i>
                    </button>
                  }
                </div>
              }
              <div class="hci-footer">
                <button class="hcf-btn view" (click)="viewAssignment(a)"><i class="bi bi-eye-fill me-1"></i>View</button>
                <button class="hcf-btn edit" (click)="editAssignment(a)"><i class="bi bi-pencil-fill me-1"></i>Edit</button>
                @if (a.status === 'Draft') {
                  <button class="hcf-btn pub" (click)="publishDraft(a)" title="Publish and notify students"><i class="bi bi-megaphone-fill"></i></button>
                } @else if (a.status === 'Published') {
                  <button class="hcf-btn remind" (click)="sendReminder(a)" title="Remind students who have not submitted"><i class="bi bi-bell-fill"></i></button>
                }
                <button class="hcf-btn delete" (click)="confirmDelete(a.id, a.title)"><i class="bi bi-trash3-fill"></i></button>
              </div>
            </div>
          }
        </div>
      }
    }

    @if (hwMode() === 'form') {
      <div class="hw-card">
        <div class="hc-header"><i class="bi bi-journal-plus me-2"></i>{{ editingHwId() !== null ? 'Edit Homework' : 'Assign Homework' }}</div>
        <div class="hc-body">
          <div class="row g-3 mb-3">
            <div class="col-md-8"><label class="form-label fw-semibold">Title <span class="req">*</span></label>
              <input class="form-control" [(ngModel)]="hwForm.title" placeholder="e.g. Numericals on Newton’s Laws"
                [class.is-invalid]="hwSubmitted && !hwForm.title?.trim()"></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Subject <span class="req">*</span></label>
              <select class="form-select" [(ngModel)]="hwForm.subject">@for (s of subjectOpts; track s) { <option [value]="s.code">{{ s.name }}</option> }</select></div>
            <div class="col-md-5"><label class="form-label fw-semibold">Class <span class="req">*</span></label>
              <select class="form-select" [(ngModel)]="hwForm.class_name" [class.is-invalid]="hwSubmitted && !hwForm.class_name">
                <option value="">-- Select --</option>
                @for (c of classOptions; track c) { <option [value]="c.code">{{ c.name }}</option> }
              </select></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Section</label>
              <select class="form-select" [(ngModel)]="hwForm.section">@for (s of sectionOpts; track s) { <option [value]="s.name">{{ s.name }}</option> }</select></div>
            <div class="col-md-5"><label class="form-label fw-semibold">Teacher</label>
              <!-- <input class="form-control" [(ngModel)]="hwForm.teacher_name"> -->
              <select class="form-select" [(ngModel)]="hwForm.teacher_name">
                <option value="">-- Select --</option>
                @for (t of teachers; track t) { <option>{{ t.first_name + ' ' + t.last_name }}</option> }
              </select>
            </div>
            <div class="col-md-3"><label class="form-label fw-semibold">Assigned date</label>
              <input type="date" class="form-control" [(ngModel)]="hwForm.assigned_date"></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Due date <span class="req">*</span></label>
              <input type="date" class="form-control" [(ngModel)]="hwForm.due_date" [class.is-invalid]="hwSubmitted && !hwForm.due_date"></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Due time</label>
              <input type="time" class="form-control" [(ngModel)]="hwForm.due_time"></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Max marks</label>
              <input type="number" class="form-control" [(ngModel)]="hwForm.max_marks" min="1"></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Priority</label>
              <select class="form-select" [(ngModel)]="hwForm.priority">@for (p of priorities; track p) { <option [value]="p">{{ p }}</option> }</select></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Status</label>
              <select class="form-select" [(ngModel)]="hwForm.status">@for (s of hwStatuses; track s) { <option [value]="s">{{ s }}</option> }</select></div>
            <div class="col-12"><label class="form-label fw-semibold">Instructions <span class="req">*</span></label>
              <textarea class="form-control" rows="3" [(ngModel)]="hwForm.instructions" maxlength="600"
                placeholder="What students should do, how to submit, marking notes…"
                [class.is-invalid]="hwSubmitted && !hwForm.instructions?.trim()"></textarea></div>
            <div class="col-12">
              <label class="form-label fw-semibold">Assignment documents</label>
              <label class="file-drop">
                <input type="file" class="d-none" multiple [accept]="acceptedFiles" (change)="onAssignmentFiles($event)">
                <i class="bi bi-cloud-arrow-up-fill"></i>
                <span>Click to upload worksheets, rubrics, or resources</span>
                <small>PDF, Word, Excel, PowerPoint, images, ZIP · max 8 MB each · up to {{ maxAssignmentDocs }} files</small>
              </label>
              @if (hwFormDocs().length) {
                <div class="file-list mt-2">
                  @for (doc of hwFormDocs(); track doc.name; let i = $index) {
                    <div class="file-row">
                      <i class="bi {{ fileIcon(doc) }}"></i>
                      <div class="flex-grow-1">
                        <div class="fw-semibold small">{{ doc.name }}</div>
                        <div class="file-size">{{ formatSize(doc.size) }}</div>
                      </div>
                      <button type="button" class="btn btn-sm btn-outline-primary" (click)="downloadDocument(doc, $event)"><i class="bi bi-download"></i></button>
                      <button type="button" class="btn btn-sm btn-outline-danger" (click)="removeAssignmentDoc(i)"><i class="bi bi-x-lg"></i></button>
                    </div>
                  }
                </div>
              }
            </div>
            <div class="col-12">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="notifyOnSave" [(ngModel)]="hwForm.notify_on_save">
                <label class="form-check-label" for="notifyOnSave">Inform students in this class when saved (inbox notification)</label>
              </div>
              <small class="text-muted">{{ studentsForClass(hwForm.class_name ?? '').length }} student{{ studentsForClass(hwForm.class_name ?? '').length === 1 ? '' : 's' }} in the selected class will receive the assignment.</small>
            </div>
          </div>
          <div class="d-flex justify-content-end gap-3">
            <button class="btn btn-outline-secondary px-4" (click)="hwMode.set('list')">Cancel</button>
            <button class="btn hw-btn-primary px-5" (click)="saveAssignment()">
              <i class="bi bi-check-circle-fill me-2"></i>{{ editingHwId() !== null ? 'Update' : 'Assign' }} Homework
            </button>
          </div>
        </div>
      </div>
    }

    @if (hwMode() === 'detail' && getViewAssignment()) {
      @let ev = getViewAssignment()!;
      <div class="row g-4">
        <div class="col-lg-4">
          <div class="hw-card">
            <div class="hc-detail-header" [style.background]="'linear-gradient(135deg,' + getSubjectColor(ev.subject) + ',' + getSubjectColor(ev.subject) + '99)'">
              <div class="edh-type">{{ dataService.getSubjectName(ev.subject) }}</div>
              <h5 class="edh-name">{{ ev.title }}</h5>
              <!-- <span class="{{ getHwStatusClass(ev.status) }}">{{ ev.status }}</span> -->
            </div>
            <div class="hc-body">
              <div class="detail-list">
                <div class="dl-row"><span>Class</span><span class="fw-semibold">{{ dataService.getClassName(ev.class_name) }} - {{ ev.section }}</span></div>
                <div class="dl-row"><span>Teacher</span><span>{{ ev.teacher_name }}</span></div>
                <div class="dl-row"><span>Assigned</span><span>{{ formatDate(ev.assigned_date) }}</span></div>
                <div class="dl-row"><span>Due</span><span>{{ formatDate(ev.due_date) }} {{ formatTime(ev.due_time) }}</span></div>
                <div class="dl-row"><span>Max marks</span><span class="fw-bold text-primary">{{ ev.max_marks }}</span></div>
                <div class="dl-row"><span>Priority</span><span class="{{ getPriorityClass(ev.priority) }}">{{ ev.priority }}</span></div>
                <div class="dl-row"><span>Status</span><span class="{{ getHwStatusClass(ev.status) }}">{{ ev.status }}</span></div>
                <div class="dl-row"><span>Students</span><span>{{ submissionCount(ev.id) }}</span></div>
              </div>
              @if (ev.documents && ev.documents.length && ev.documents[0].name) {
                <div class="file-list mt-3">
                  @for (doc of ev.documents; track doc.name) {
                    <div class="file-row">
                      <i class="bi {{ fileIcon(doc) }}"></i>
                      <div class="flex-grow-1">
                        <div class="fw-semibold small">{{ doc.name }}</div>
                        <div class="file-size">{{ formatSize(doc.size) }}</div>
                      </div>
                      <button type="button" class="btn btn-sm btn-outline-primary" (click)="downloadDocument(doc, $event)">
                        <i class="bi bi-download me-1"></i>
                      </button>
                    </div>
                  }
                </div>
              }
              <p class="small text-muted mt-3 mb-3">{{ ev.instructions }}</p>
              <div class="d-flex gap-2 flex-wrap">
                <button class="btn hw-btn-primary btn-sm flex-grow-1" (click)="editAssignment(ev)"><i class="bi bi-pencil-fill me-1"></i>Edit</button>
                @if (ev.status === 'Draft') {
                  <button class="btn btn-success btn-sm" (click)="publishDraft(ev)"><i class="bi bi-megaphone me-1"></i>Publish</button>
                }
                @if (ev.status === 'Published') {
                  <button class="btn btn-outline-warning btn-sm" (click)="sendReminder(ev)"><i class="bi bi-bell me-1"></i>Remind</button>
                  <button class="btn btn-outline-secondary btn-sm" (click)="closeAssignment(ev)">Close</button>
                }
                <button class="btn btn-outline-danger btn-sm" (click)="confirmDelete(ev.id, ev.title)"><i class="bi bi-trash3"></i></button>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-8">
          <div class="hw-card">
            <div class="hc-header"><i class="bi bi-people-fill me-2"></i>Class Submissions</div>
            <div class="hc-body p-0">
              <table class="hw-table">
                <thead><tr><th>Roll</th><th>Student</th><th>Status</th><th>Submitted</th><th>Marks</th></tr></thead>
                <tbody>
                  @for (s of submissions().filter(x => x.homework_id === ev.id); track s.id) {
                    <tr>
                      <td class="small text-muted">{{ s.roll_no }}</td>
                      <td class="fw-semibold small">{{ s.student_name }}</td>
                      <td><span class="{{ getSubStatusClass(effectiveStatus(s, ev)) }}">{{ effectiveStatus(s, ev) }}</span></td>
                      <td class="small">{{ s.submitted_at ? formatStamp(s.submitted_at) : '—' }}</td>
                      <td class="small">{{ s.marks_obtained !== null ? s.marks_obtained + ' / ' + ev.max_marks : '—' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    }
  }

  <!-- ===================== TEACHER · REVIEW ===================== -->
  @if (role() === 'teacher' && activeTab() === 'review') {
    <div class="hw-card mb-3"><div class="hc-body">
      <div class="row g-3 align-items-end">
        <div class="col-md-5"><label class="form-label small fw-semibold text-muted">Assignment</label>
          <select class="form-select" [ngModel]="reviewHwId()" (ngModelChange)="reviewHwId.set(+$event)">
            @for (a of assignments(); track a.id) { <option [value]="a.id">{{ a.title }} · {{ a.class_name }}</option> }
          </select></div>
        <div class="col-md-3"><label class="form-label small fw-semibold text-muted">Status</label>
          <select class="form-select" [ngModel]="reviewStatus()" (ngModelChange)="reviewStatus.set($event)">
            <option value="">All</option>
            @for (s of submissionStatuses; track s) { <option [value]="s">{{ s }}</option> }
          </select></div>
        <div class="col-md-4"><label class="form-label small fw-semibold text-muted">Search student</label>
          <input class="form-control" placeholder="Name or roll no…" [ngModel]="reviewSearch()" (ngModelChange)="reviewSearch.set($event)">
        </div>
      </div>
    </div></div>

    <div class="hw-stats-row mb-3">
      <div class="hsr-item compact"><div class="hsr-val">{{ reviewStats().total }}</div><div class="hsr-label">Students</div></div>
      <div class="hsr-item compact"><div class="hsr-val text-success">{{ reviewStats().submitted }}</div><div class="hsr-label">Submitted</div></div>
      <div class="hsr-item compact"><div class="hsr-val text-warning">{{ reviewStats().pending }}</div><div class="hsr-label">Pending</div></div>
      <div class="hsr-item compact"><div class="hsr-val text-purple">{{ reviewStats().graded }}</div><div class="hsr-label">Graded</div></div>
      <div class="hsr-item compact"><div class="hsr-val">{{ reviewStats().avg || '—' }}</div><div class="hsr-label">Avg marks</div></div>
    </div>

    @if (filteredSubmissions().length === 0) {
      <div class="empty-state"><i class="bi bi-inbox"></i><p>No submissions for this filter</p></div>
    } @else {
      <div class="hw-card">
        <div class="hc-body p-0">
          <table class="hw-table">
            <thead><tr><th>Roll</th><th>Student</th><th>Status</th><th>File</th><th>Submitted</th><th>Marks</th><th></th></tr></thead>
            <tbody>
              @for (s of filteredSubmissions(); track s.id) {
                @let hw = getHw(s.homework_id);
                <tr>
                  <td class="small text-muted">{{ s.roll_no }}</td>
                  <td class="fw-semibold small">{{ s.student_name }}</td>
                  <td><span class="{{ getSubStatusClass(hw ? effectiveStatus(s, hw) : s.status) }}">{{ hw ? effectiveStatus(s, hw) : s.status }}</span></td>
                  <td class="small">
                    @if (s.file) {
                      <button type="button" class="doc-chip" (click)="downloadDocument(s.file!, $event)" title="Download {{ s.file.name }}">
                        <i class="bi {{ fileIcon(s.file) }} me-1"></i>{{ s.file.name }}
                        <i class="bi bi-download ms-1"></i>
                      </button>
                    } @else { — }
                  </td>
                  <td class="small">{{ s.submitted_at ? formatStamp(s.submitted_at) : '—' }}</td>
                  <td class="small fw-semibold">{{ s.marks_obtained !== null ? s.marks_obtained : '—' }}</td>
                  <td class="text-end">
                    @if (s.status === 'Submitted' || s.status === 'Late' || s.status === 'Graded' || s.status === 'Returned') {
                      <button class="btn btn-sm hw-btn-primary" (click)="openGrade(s)"><i class="bi bi-award me-1"></i>Review</button>
                    } @else {
                      <span class="text-muted small">Not submitted</span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }
  }

  <!-- ===================== STUDENT · MY HOMEWORK ===================== -->
  @if (role() === 'student' && activeTab() === 'my-homework') {
    <div class="hw-card mb-3">
      <div class="hc-body">
        <div class="row g-3 align-items-start">
          <div class="student-chip col-md-3 me-5">
            <div class="sc-av">{{ currentStudent().name.charAt(0) }}</div>
            <div>
              <div class="fw-semibold">{{ currentStudent().name }}</div>
              <div class="small text-muted">
                Class {{ dataService.getClassName(currentStudent().class_name) }},  Roll No. {{ currentStudent().roll_no }}</div>
            </div>
          </div>
          <div class="col-md-5">
            <label class="form-label small fw-semibold text-muted">Search</label>
            <input class="form-control" placeholder="Title or subject…" [ngModel]="mySearch()" (ngModelChange)="mySearch.set($event)">
          </div>
          <div class="col-md-3">
            <label class="form-label small fw-semibold text-muted">Status</label>
            <select class="form-select" [ngModel]="myStatusFilter()" (ngModelChange)="myStatusFilter.set($event)">
              <option value="">All</option>
              @for (s of submissionStatuses; track s) { <option [value]="s">{{ s }}</option> }
            </select>
          </div>
        </div>
      </div>
    </div>

    @if (myHomework().length === 0) {
      <div class="empty-state"><i class="bi bi-emoji-smile"></i><p>No homework for this student</p></div>
    } @else {
      <div class="hw-grid">
        @for (row of myHomework(); track row.sub.id) {
          @let a = row.hw!;
          @let s = row.sub;
          @let st = effectiveStatus(s, a);
          <div class="hw-card-item" [style.--hw-color]="getSubjectColor(a.subject)" [class.is-overdue]="isOverdue(a, s)">
            <div class="hci-top">
              <div class="hci-subject" [style.color]="getSubjectColor(a.subject)">{{ a.subject }}</div>
              <span class="{{ getSubStatusClass(st) }}">{{ st }}</span>
            </div>
            <h5 class="hci-name">{{ a.title }}</h5>
            <div class="hci-dates">
              <span [class.text-danger]="isOverdue(a, s) || st === 'Late'"><i class="bi bi-clock me-1"></i>{{ dueLabel(a) }}</span>
              <span class="text-muted">{{ formatDate(a.due_date) }} {{ formatTime(a.due_time) }}</span>
            </div>
            <p class="hci-desc">{{ a.instructions }}</p>
            @if (a.documents && a.documents.length && a.documents[0].name) {
              <div class="hci-docs">
                @for (doc of a.documents; track doc.name) {
                  <button type="button" class="doc-chip" (click)="downloadDocument(doc, $event)" title="Download {{ doc.name }}">
                    <i class="bi {{ fileIcon(doc) }} me-1"></i>{{ doc.name }}
                    <i class="bi bi-download ms-1"></i>
                  </button>
                }
              </div>
            }
            @if (s.marks_obtained !== null) {
              <div class="marks-pill">{{ s.marks_obtained }} / {{ a.max_marks }} · {{ s.teacher_remark || 'Graded' }}</div>
            }
            <div class="hci-footer">
              @if (s.status === 'Assigned') {
                <button class="hcf-btn edit" (click)="markInProgress(s)"><i class="bi bi-play-fill me-1"></i>Start</button>
              }
              <button class="hcf-btn view" (click)="openUpdate(s)">
                <i class="bi bi-pencil-square me-1"></i>{{ s.status === 'Graded' ? 'View' : 'Update / Submit' }}
              </button>
            </div>
          </div>
        }
      </div>
    }
  }

  <!-- ===================== STUDENT · INBOX ===================== -->
  @if (role() === 'student' && activeTab() === 'inbox') {
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="small text-muted">{{ currentStudent().name }} · {{ myAlerts().length }} messages</div>
      @if (unreadAlertCount() > 0) {
        <button class="btn btn-sm btn-outline-primary" (click)="markAllAlertsRead()">Mark all read</button>
      }
    </div>
    @if (myAlerts().length === 0) {
      <div class="empty-state"><i class="bi bi-bell-slash"></i><p>No homework notifications yet</p></div>
    } @else {
      <div class="alert-list">
        @for (n of myAlerts(); track n.id) {
          <button class="alert-item" [class.unread]="n.unread" (click)="openHomeworkFromAlert(n)">
            <div class="alert-icon {{ getAlertBg(n.kind) }}"><i class="bi {{ getAlertIcon(n.kind) }}"></i></div>
            <div class="flex-grow-1 text-start">
              <div class="fw-semibold">{{ n.title }}</div>
              <div class="small text-muted">{{ n.msg_text }}</div>
            </div>
            <div class="small text-muted text-nowrap">{{ n.msg_time }}</div>
          </button>
        }
      </div>
    }
  }

</div>
